import { CircleNotchIcon } from '@phosphor-icons/react';
import { useGT } from 'gt-react';
import type { WorkbookRun } from '@erp/shared-types';
import { WorkbookRunStatus, WorkbookRunningTotal } from './WorkbookRunHistory';

/**
 * The footer's word on the latest Run, the one place a Run's progress shows:
 * while it waits or runs, a spinner and the step it is on — loading data,
 * computing a Worksheet, saving results — with its running time; once it
 * ends, how it ended.
 */
export function WorkbookRunFooterStatus({
  run,
  definition,
}: {
  run: Pick<WorkbookRun, 'status' | 'timing' | 'error'>;
  definition: { sheets: readonly { id: string; name: string }[] };
}) {
  const gt = useGT();
  const inProgress = run.status === 'queued' || run.status === 'running';
  if (!inProgress)
    return (
      <span>
        <WorkbookRunStatus status={run.status} />
        {run.error ? `: ${run.error}` : ''}
      </span>
    );
  const step = run.status === 'running' ? run.timing?.step : undefined;
  const sheetName =
    step?.kind === 'sheet'
      ? definition.sheets.find(({ id }) => id === step.sheetId)?.name
      : undefined;
  const label =
    step?.kind === 'sources'
      ? gt('Loading data…', { context: 'workbook run footer status' })
      : step?.kind === 'saving'
        ? gt('Saving results…', { context: 'workbook run footer status' })
        : sheetName
          ? gt('Computing {name}…', {
              context: 'workbook run footer status: the worksheet computing',
              name: sheetName,
            })
          : undefined;
  return (
    <span className="flex items-center gap-1">
      <CircleNotchIcon
        className="size-3 animate-spin motion-reduce:animate-none"
        aria-hidden
      />
      {/* Only the step is announced: the ticking time would re-announce it every second. */}
      <span role="status">
        {label ?? <WorkbookRunStatus status={run.status} />}
      </span>
      <WorkbookRunningTotal run={run} />
    </span>
  );
}
